import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDays, ClipboardCheck, MessageCircle, Users, Briefcase, ChevronRight } from 'lucide-react';
import ScrollReveal from '../effects/ScrollReveal';

const steps = [
  {
    icon: CalendarDays,
    title: 'Structured Learning',
    subtitle: 'Week-wise Roadmap',
    points: ['Week-wise curriculum', 'Daily assignments', 'Progress dashboard'],
    color: 'from-yellow-400/20 to-yellow-400/5',
    num: '01',
  },
  {
    icon: ClipboardCheck,
    title: 'Accountability',
    subtitle: 'Stay on Track',
    points: ['Daily / weekly follow-ups', 'Mentor tracking', 'Full team support'],
    color: 'from-yellow-400/20 to-yellow-400/5',
    num: '02',
  },
  {
    icon: MessageCircle,
    title: 'Doubt Support',
    subtitle: '24/7 Help',
    points: ['24/7 doubt solving', 'Dedicated mentors', 'Quick resolution'],
    color: 'from-yellow-400/20 to-yellow-400/5',
    num: '03',
  },
  {
    icon: Users,
    title: 'Peer Learning',
    subtitle: 'Batch Community',
    points: ['Batch community', 'Networking & collab', 'Group projects'],
    color: 'from-yellow-400/20 to-yellow-400/5',
    num: '04',
  },
  {
    icon: Briefcase,
    title: 'Hiring Connect',
    subtitle: 'Real Placements',
    points: ['Hiring drives', 'Company referrals', 'Job updates'],
    color: 'from-yellow-400/20 to-yellow-400/5',
    num: '05',
  },
];

export default function TrainingSystem() {
  const [hovered, setHovered] = useState(null);

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Bg accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-muted/20 to-transparent pointer-events-none" />
      <motion.div
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute bottom-0 right-[10%] w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(254,210,2,0.08) 0%, transparent 65%)' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        <ScrollReveal variant="fadeUp" className="text-center mb-16">
          <span className="inline-block text-primary text-sm font-bold tracking-widest uppercase mb-3 px-4 py-1.5 rounded-full glass-yellow">Our Process</span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-4">
            How We <span className="text-primary relative">
              Train You
              <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.6 }}
                className="absolute -bottom-1.5 left-0 right-0 h-0.5 bg-primary rounded-full origin-left" />
            </span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-lg mx-auto text-lg font-body">
            A step-by-step system designed for real results, not just certificates.
          </p>
        </ScrollReveal>

        {/* Timeline */}
        <div className="relative">
          {/* Animated connector line */}
          <div className="hidden lg:block absolute top-[52px] left-[10%] right-[10%] h-px bg-border/50">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
              
              className="h-full bg-gradient-to-r from-primary/50 via-secondary to-primary/50 origin-left"
            />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {steps.map((step, i) => {
              const StepIcon = step.icon;
              const isHovered = hovered === i;
              return (
                <ScrollReveal key={step.title} variant="fadeUp" delay={i * 0.1}>
                  <motion.div
                    onHoverStart={() => setHovered(i)}
                    onHoverEnd={() => setHovered(null)}
                    whileHover={{ y: -8 }}
                    transition={{ type: 'spring', stiffness: 280, damping: 18 }}
                    className={`glass rounded-2xl border border-white/80 p-6 shadow-sm transition-all duration-300 h-full relative overflow-hidden group cursor-default ${
                      isHovered ? 'shadow-xl shadow-black/8 border-primary/30' : ''
                    }`}
                  >
                    {/* Hover bg */}
                    <AnimatePresence>
                      {isHovered && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none"
                        />
                      )}
                    </AnimatePresence>

                    {/* Step number */}
                    <div  className="absolute top-4 right-4 font-heading font-black text-4xl text-primary/40 dark:text-sky-400/50 leading-none select-none">
                      {step.num}
                    </div>

                    {/* Icon */}
                    <motion.div
                      animate={isHovered ? { scale: 1.15, rotate: -10 } : { scale: 1, rotate: 0 }}
                      className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center shadow-md shadow-primary/25 mb-4"
                    >
                      <StepIcon className="w-5 h-5 text-primary-foreground" />
                    </motion.div>

                    <h3 className="font-heading font-bold text-sm mb-0.5">{step.title}</h3>
                    <p className="text-[11px] text-primary font-semibold mb-3 font-body">{step.subtitle}</p>

                    <ul className="space-y-2">
                      {step.points.map((p, pi) => (
                        <motion.li
                          key={p}
                          initial={{ opacity: 0, x: -8 }}
                          animate={isHovered ? { opacity: 1, x: 0 } : { opacity: 0.75, x: 0 }}
                          transition={{ delay: pi * 0.05 }}
                          className="flex items-start gap-2 text-xs text-muted-foreground font-body"
                        >
                          <motion.span
                            animate={isHovered ? { scale: 1.4, backgroundColor: 'hsl(47,99%,50%)' } : { scale: 1 }}
                            className="w-1.5 h-1.5 rounded-full bg-primary/50 mt-1 flex-shrink-0"
                          />
                          {p}
                        </motion.li>
                      ))}
                    </ul>

                    {/* Arrow for non-last */}
                    {i < steps.length - 1 && (
                      <div className="hidden lg:block absolute -right-2.5 top-1/2 -translate-y-1/2 z-10">
                        <motion.div
                          animate={{ x: [0, 4, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                        >
                          <ChevronRight className="w-5 h-5 text-primary/40" />
                        </motion.div>
                      </div>
                    )}
                  </motion.div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}